import { logUsage } from 'owa-analytics';
import type { LinkIdContainer } from 'owa-attachment-link-common';
import { lazyInsertLinkPlaceholdersRemoveOrRehydrate } from 'owa-insert-link';
import { AnchorElementsSource } from 'owa-link-data';
import type LinkPlugin from '../plugin/LinkPlugin';
import { ANCHOR_TAG_NAME, SPINNER_CLASSNAME } from './constants';
import processAnchorElements from './processAnchorElementsFromHtmlContent';
import { shareLinkComposeHandling } from './shareLinkComposeHandling';
import { OWA_REFERENCE_ATTACHMENTS_ID } from './shouldProcessLink';

const CLASS_QUERY_PREFIX = '.';
const ID_QUERY_PREFIX = '#';

export async function processAllLinksInEditor(
    linkPlugin: LinkPlugin,
    linkIdContainer: LinkIdContainer,
    linkIdsWithSpinner: string[],
    shouldChangeLinkId: boolean,
    anchorElementsSource: AnchorElementsSource,
    targetWindow: Window,
    isCalendar: boolean
) {
    if (!linkPlugin.editor) {
        logUsage('processAllLinksInNullEditor', { source: anchorElementsSource });
        return;
    }
    if (
        anchorElementsSource === AnchorElementsSource.EditorLoadedOrDraftOrReply ||
        anchorElementsSource === AnchorElementsSource.Undo
    ) {
        if (
            await shareLinkComposeHandling(
                linkPlugin,
                linkIdContainer,
                linkIdsWithSpinner,
                targetWindow,
                isCalendar
            )
        ) {
            return;
        }
    }

    if (!linkPlugin.editor) {
        return;
    }

    const allLinks = linkPlugin.editor.queryElements(ANCHOR_TAG_NAME);
    const hiddenReferenceAttachments = linkPlugin.editor.queryElements(
        ID_QUERY_PREFIX + OWA_REFERENCE_ATTACHMENTS_ID
    );

    removeInvalidSpinner(linkPlugin, linkIdsWithSpinner);
    await processAnchorElements(
        allLinks,
        hiddenReferenceAttachments,
        anchorElementsSource,
        linkPlugin,
        linkIdContainer,
        linkIdsWithSpinner,
        shouldChangeLinkId,
        false /* shouldTryBeautify */,
        targetWindow,
        isCalendar
    );

    // When user switches between different compose tabs, we still have the placeholder view states in the store if the links haven't been successfully inserted.
    // When user comes back to the original compose tab, we need to rehydrate the placeholder of these links based on the store.
    const insertLinkPlaceholdersRemoveOrRehydrate = await lazyInsertLinkPlaceholdersRemoveOrRehydrate.import();
    insertLinkPlaceholdersRemoveOrRehydrate(
        linkPlugin.editor,
        linkPlugin.editorViewState,
        linkPlugin.getRecipientWells,
        linkIdContainer,
        linkPlugin.fromAddress,
        targetWindow
    );
}

function removeInvalidSpinner(linkPlugin: LinkPlugin, linkIdsWithSpinner: string[]) {
    linkPlugin.editor.queryElements(CLASS_QUERY_PREFIX + SPINNER_CLASSNAME, spinner => {
        if (linkIdsWithSpinner.indexOf(spinner.parentElement.id) === -1) {
            spinner.parentElement.removeChild(spinner);
        }
    });
}
